import { FiCheckCircle } from 'react-icons/fi';

const LEGEND_VARIANTS = {
    tutor: [
        {
            id: 'first-lesson',
            label: 'First lesson',
            hint: 'New student — introductory session',
            swatch: { bg: '#FFF9C4', border: '#191D51', text: '#191D51' },
        },
        {
            id: 'booked',
            label: 'Booked lesson',
            hint: 'Confirmed upcoming appointment',
            swatch: { bg: '#191D51', border: '#191D51', text: '#FFFFFF' },
        },
        {
            id: 'blocked',
            label: 'Blocked slot',
            hint: 'Manually unavailable time',
            swatch: { bg: '#CCCCCC', border: '#999999', pattern: 'blocked' },
        },
        {
            id: 'off-hours',
            label: 'Outside availability',
            hint: 'Outside your working hours',
            swatch: { bg: 'rgba(238, 238, 238, 0.85)', border: '#E5E7EB', pattern: 'muted' },
        },
    ],
    student: [
        {
            id: 'first-lesson',
            label: 'First lesson',
            hint: 'Your first session with this tutor',
            swatch: { bg: '#FFF9C4', border: '#191D51', text: '#191D51' },
        },
        {
            id: 'upcoming',
            label: 'Upcoming',
            hint: 'Scheduled lesson',
            swatch: { bg: '#191D51', border: '#191D51', text: '#FFFFFF' },
        },
        {
            id: 'completed-first',
            label: 'Completed — first lesson',
            hint: 'Finished first session with this tutor',
            swatch: { bg: '#FFF9C4', border: '#191D51', text: '#191D51', pattern: 'completed' },
        },
        {
            id: 'completed-standard',
            label: 'Completed — standard',
            hint: 'Finished lesson with check mark',
            swatch: { bg: '#191D51', border: '#191D51', text: '#FFFFFF', pattern: 'completed' },
        },
        {
            id: 'pending-validation',
            label: 'Awaiting validation',
            hint: 'Problem reported or pending admin review',
            swatch: { bg: '#F97316', border: '#F97316', text: '#FFFFFF' },
        },
        {
            id: 'canceled',
            label: 'Canceled / not paid',
            hint: 'Lesson was canceled or payment failed',
            swatch: { bg: '#DF2B47', border: '#DF2B47', text: '#FFFFFF' },
        },
    ],
};

const Swatch = ({ swatch }) => {
    const baseClass =
        'relative h-5 w-8 shrink-0 overflow-hidden rounded-md border shadow-sm';

    if (swatch.pattern === 'blocked') {
        return (
            <span
                className={baseClass}
                style={{
                    backgroundColor: swatch.bg,
                    borderColor: swatch.border,
                    backgroundImage:
                        'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(255,255,255,0.45) 3px, rgba(255,255,255,0.45) 6px)',
                }}
                aria-hidden="true"
            />
        );
    }

    if (swatch.pattern === 'muted') {
        return (
            <span
                className={baseClass}
                style={{
                    backgroundColor: swatch.bg,
                    borderColor: swatch.border,
                }}
                aria-hidden="true"
            />
        );
    }

    if (swatch.pattern === 'completed') {
        return (
            <span
                className={`${baseClass} flex items-center justify-end pr-0.5`}
                style={{
                    backgroundColor: swatch.bg,
                    borderColor: swatch.border,
                    color: swatch.text,
                }}
                aria-hidden="true"
            >
                <FiCheckCircle size={11} className="opacity-90" />
            </span>
        );
    }

    return (
        <span
            className={`${baseClass} flex items-center justify-center`}
            style={{
                backgroundColor: swatch.bg,
                borderColor: swatch.border,
                color: swatch.text,
            }}
            aria-hidden="true"
        >
            <span className="h-1.5 w-3 rounded-full bg-current opacity-80" />
        </span>
    );
};

const CalendarColorLegend = ({ variant = 'tutor', className = '' }) => {
    const items = LEGEND_VARIANTS[variant] ?? LEGEND_VARIANTS.tutor;

    return (
        <div
            className={`mt-4 rounded-xl border border-slate-100 bg-gradient-to-br from-slate-50/80 to-white px-4 py-3.5 ${className}`}
            role="list"
            aria-label="Calendar color legend"
        >
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-nt-grey">
                Legend
            </p>
            <div className="flex flex-wrap gap-2.5">
                {items.map((item) => (
                    <div
                        key={item.id}
                        role="listitem"
                        className="group flex min-w-[140px] flex-1 items-center gap-2.5 rounded-lg border border-slate-100 bg-white px-3 py-2.5 shadow-sm transition-shadow hover:shadow-md sm:min-w-[160px] sm:max-w-[220px]"
                        title={item.hint}
                    >
                        <Swatch swatch={item.swatch} />
                        <div className="min-w-0">
                            <p className="truncate text-sm font-medium leading-tight text-nt-blue">
                                {item.label}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] leading-tight text-nt-grey">
                                {item.hint}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export { CalendarColorLegend, LEGEND_VARIANTS };
